import React, { useState, useEffect, useCallback } from 'react';
import { SpectatorList } from './SpectatorList';
import type { ActiveGame } from './SpectatorGameCard';
import { ApiService } from '../../services/apiService';
import type { AuthState } from '../../game/types';
import PageCloseButton from '../PageCloseButton';
import './SpectatorListPage.css';

interface SpectatorListPageProps {
  onGameClick: (gameId: string) => void;
  onBack: () => void;
  spectatorTimers: Record<string, { white: number; black: number; activePlayer: 'white' | 'black' | null }>;
  authState: AuthState;
}

export const SpectatorListPage: React.FC<SpectatorListPageProps> = ({ onGameClick, onBack, spectatorTimers, authState }) => {
  const [games, setGames] = useState<ActiveGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const fetchActiveGames = useCallback(async (isInitialLoad: boolean = false) => {
    try {
      // Only show loading on initial load, not on periodic refreshes
      if (isInitialLoad) {
        setLoading(true);
      }
      setError('');
      
      // Fetch active games from API using ApiService
      const result = await ApiService.getActiveGames();
      
      if (result.error) {
        throw new Error(result.error);
      }

      const data = { games: result.games };
      const currentUserId = authState.user?.id;
      
      // Transform backend data to ActiveGame format and filter out user's own games
      const activeGames: ActiveGame[] = (data.games || [])
        .filter((game: any) => {
          // Filter out games where the current user is a player
          if (currentUserId) {
            const whiteUserId = game.whitePlayer?.userId || game.players?.white?.userId;
            const blackUserId = game.blackPlayer?.userId || game.players?.black?.userId;
            return whiteUserId !== currentUserId && blackUserId !== currentUserId;
          }
          return true; // If no userId, show all games (shouldn't happen for authenticated users)
        })
        .map((game: any) => {
        const gameId = game.gameId || game.id;
        // Use real-time timer data if available, otherwise use backend data
        // Access current spectatorTimers from props (will be merged in useEffect)
        const timerData = spectatorTimers[gameId] || {
          white: game.timers?.white || 600,
          black: game.timers?.black || 600,
          activePlayer: game.activeTimer || game.currentPlayer || null
        };

        return {
          gameId,
          whitePlayer: game.whitePlayer?.name || game.players?.white?.name || game.whitePlayer || 'White',
          blackPlayer: game.blackPlayer?.name || game.players?.black?.name || game.blackPlayer || 'Black',
          timeControl: game.timerSettings 
            ? `${game.timerSettings.minutesPerPlayer || 10}+${game.timerSettings.incrementSeconds || 0}`
            : '10+0',
          whiteTimer: timerData.white,
          blackTimer: timerData.black,
          activePlayer: timerData.activePlayer,
          moveCount: game.moveCount || game.moveHistory?.length || 0,
          spectatorCount: game.spectatorCount || 0
        };
      });

      // Sort by time control (shortest first), then by move count
      activeGames.sort((a, b) => {
        const aTime = parseInt(a.timeControl.split('+')[0]) || 10;
        const bTime = parseInt(b.timeControl.split('+')[0]) || 10;
        if (aTime !== bTime) {
          return aTime - bTime;
        }
        return a.moveCount - b.moveCount;
      });

      // Smart merge: preserve existing game objects when possible to prevent re-renders
      setGames(prevGames => {
        // Create a map of existing games by gameId for quick lookup
        const existingGamesMap = new Map(prevGames.map(game => [game.gameId, game]));
        const newGamesMap = new Map(activeGames.map(game => [game.gameId, game]));
        
        // Build result array preserving existing objects when data hasn't changed
        const mergedGames = activeGames.map(newGame => {
          const existingGame = existingGamesMap.get(newGame.gameId);
          
          // If game exists and only timer/moveCount/spectatorCount changed, preserve object
          // Only create new object if game info (players, timeControl) changed
          if (existingGame && 
              existingGame.whitePlayer === newGame.whitePlayer &&
              existingGame.blackPlayer === newGame.blackPlayer &&
              existingGame.timeControl === newGame.timeControl) {
            // Update only changed fields, preserve object reference
            if (existingGame.whiteTimer !== newGame.whiteTimer ||
                existingGame.blackTimer !== newGame.blackTimer ||
                existingGame.activePlayer !== newGame.activePlayer ||
                existingGame.moveCount !== newGame.moveCount ||
                existingGame.spectatorCount !== newGame.spectatorCount) {
              return {
                ...existingGame,
                whiteTimer: newGame.whiteTimer,
                blackTimer: newGame.blackTimer,
                activePlayer: newGame.activePlayer,
                moveCount: newGame.moveCount,
                spectatorCount: newGame.spectatorCount
              };
            }
            // No changes, return existing object
            return existingGame;
          }
          
          // New game or game info changed, return new object
          return newGame;
        });
        
        // Check if order changed
        const orderChanged = mergedGames.length !== prevGames.length ||
          mergedGames.some((game, index) => 
            index >= prevGames.length || game.gameId !== prevGames[index].gameId
          );
        
        // Only update if something actually changed
        if (orderChanged || mergedGames.some((game, index) => game !== prevGames[index])) {
          return mergedGames;
        }
        
        // Nothing changed, return previous array
        return prevGames;
      });
    } catch (err: any) {
      console.error('Error fetching active games:', err);
      setError(err.message || 'Failed to load active games');
      if (isInitialLoad) {
        setGames([]);
      }
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.user?.id]); // spectatorTimers intentionally excluded - updated separately via useEffect

  useEffect(() => {
    // Initial load with loading state
    fetchActiveGames(true);
    
    // Refresh games list every 10 seconds (less frequent to reduce flicker)
    // Only refresh to pick up new games or ended games, timers update via socket
    const interval = setInterval(() => {
      fetchActiveGames(false); // No loading state on refresh
    }, 10000); // Increased from 5 to 10 seconds

    return () => clearInterval(interval);
  }, [fetchActiveGames]);

  // Update games when timers change - use a more efficient update
  useEffect(() => {
    if (games.length > 0) {
      setGames(prevGames => {
        const updatedGames = prevGames.map(game => {
          const timerData = spectatorTimers[game.gameId];
          if (timerData) {
            // Only update if timer data actually changed to prevent unnecessary re-renders
            if (game.whiteTimer !== timerData.white || 
                game.blackTimer !== timerData.black || 
                game.activePlayer !== timerData.activePlayer) {
              return {
                ...game,
                whiteTimer: timerData.white,
                blackTimer: timerData.black,
                activePlayer: timerData.activePlayer
              };
            }
          }
          return game; // Return same object reference if no changes
        });
        // Only update state if something actually changed
        const hasChanges = updatedGames.some((game, index) => game !== prevGames[index]);
        return hasChanges ? updatedGames : prevGames;
      });
    }
  }, [spectatorTimers, games.length]);

  return (
    <div className="spectator-list-page">
      <div className="spectator-list-header">
        <PageCloseButton onClick={onBack} />
        <h1>🔭 YugoVision</h1>
        <p>Select a game to watch live</p>
      </div>
      
      {error && (
        <div style={{ 
          padding: '16px', 
          backgroundColor: '#ffebee', 
          color: '#c62828', 
          borderRadius: '4px',
          margin: '20px',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}
      
      <SpectatorList 
        games={games} 
        onGameClick={onGameClick}
        loading={loading}
      />
    </div>
  );
};
